if (typeof Alpine === 'undefined') {
  window.addEventListener('alpine:init', registerCartStore)
} else {
  registerCartStore()
}

/**
 * Registers an Alpine store wrapper around the Shopify Cart Ajax API.
 */
function registerCartStore() {
  const rootRoute = Shopify?.routes.root ?? '/'
  const client = newCartApiClient(rootRoute)
  const store = newCartStore(client, subscribe)
  Alpine.store('cart', store)
  Alpine.effect(() => {
    const cartData = Alpine.store('cart').data
    if (!cartData) return

    publish(PUB_SUB_EVENTS.cartUpdate, {
      cartData,
      source: 'cart-store'
    })
  })

  // ── GWP cleanup ────────────────────────────────────────────────────────────
  // Remove gift-with-purchase items whenever no qualifying Downshift sparkling
  // water or cocktail product remains in the cart.
  // Gummies, tote bags, stickers and other non-qualifying items never count.

  const GWP_HANDLES = new Set([
    'shift-society-welcome-pack',
    'free-shipping',
    'free-gummies',
    'koozie-gwp'
  ])

  function _isGwpItem(item) {
    return (
      (item.properties && item.properties._gwp) ||
      GWP_HANDLES.has((item.handle || '').toLowerCase())
    )
  }

  function _isQualifyingItem(item) {
    if (_isGwpItem(item)) return false
    const handle = (item.handle || '').toLowerCase()
    if (handle.includes('gumm')) return false   // gummy/gummies never qualify
    if (handle.endsWith('-gwp')) return false
    return (
      handle.includes('downshift') ||
      handle.includes('cocktail') ||
      handle.includes('paloma') ||
      handle.includes('margarita') ||
      handle.includes('ginger-sour')
    )
  }

  let _gwpCleaning = false

  async function _runGwpCleanup() {
    if (_gwpCleaning) return
    const cartData = Alpine.store('cart').data
    if (!cartData) return

    const items = cartData.items || []
    const gwpItems = items.filter(_isGwpItem)
    if (gwpItems.length === 0) return          // nothing to clean up
    if (items.some(_isQualifyingItem)) return  // qualifying item present, keep GWP

    _gwpCleaning = true
    try {
      // Use item.key (unique line-item identifier) rather than item.id (variant ID)
      // so each line item is targeted precisely even if variant IDs repeat.
      const updates = {}
      gwpItems.forEach(item => { updates[item.key] = 0 })
      await Alpine.store('cart').updateCart(updates)
    } finally {
      _gwpCleaning = false
    }
  }

  // Primary trigger: Alpine reactive effect fires whenever cart data changes.
  Alpine.effect(() => {
    Alpine.store('cart').data  // track reactive dependency
    _runGwpCleanup()
  })

  // Belt-and-suspenders: also fire on every pubsub cart update (catches changes
  // that arrive via section renders or external cart mutations).
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    setTimeout(_runGwpCleanup, 150)
  })
}

/**
 * Alpine store for Shopify cart global state
 * Subscribes to pubsub events to stay in sync with the theme.
 * Optional `sections` param allows support for AJAX section loading as well.
 * @param {Object} client - the cart API client to use
 * @param {Object} subscribe - the pubsub subscribe function
 * @param {Array} sections - list of section ids to fetch with cart API operations
 * @returns {Object} the store object to be registered with Alpine
 */
function newCartStore(client, subscribe, sections) {
  return {
    _queue: [],
    data: null,
    loading: false,

    _enqueue(fn) {
      this.loading = true
      const promise = new Promise((resolve, reject) =>
        this._queue.push(() => fn().then(resolve).catch(reject))
      )
      if (this._queue.length === 1) {
        this._dequeue()
      }
      return promise
    },

    async _dequeue() {
      const fn = this._queue[0]
      if (fn) {
        await fn()
        this._queue.shift()
        this._dequeue()
      }
      if (this._queue.length === 0) {
        this.loading = false
      }
    },

    init() {
      this.getCart()
      subscribe(PUB_SUB_EVENTS.cartUpdate, e => {
        if (e.source === 'cart-store') return
        this.data = e.cartData ?? this.data
      })
    },

    setSections(value) {
      sections = value
    },

    getCart() {
      return this._enqueue(async () => {
        this.data = await client.getCart()
        return this.data
      })
    },

    updateCart(updates) {
      return this._enqueue(async () => {
        this.data = await client.updateCart(updates, sections)
        this.sections = this.data.sections
        return this.data
      })
    },

    async addLineItems(lineItems) {
      return this._enqueue(async () => {
        const result = await client.addLineItems(lineItems, sections)
        this.sections = result.sections
        // The last add operation in the queue should refetch the cart
        if (this._queue.length === 1) {
          this.data = await client.getCart()
        }
        return result
      })
    },

    async updateLineItem(input) {
      return this._enqueue(async () => {
        this.data = await client.updateLineItem(input, sections)
        this.sections = this.data.sections
        return this.data
      })
    }
  }
}

/**
 * Client for Shopify Cart Ajax API.
 * The `baseUrl` param is for compatibility with Shopify markets localization
 * @param {string} baseUrl - the base URL of the cart API
 * @returns {Object} the cart API client
 * @see {@link https://shopify.dev/docs/api/ajax/reference/cart | Cart API Reference }
 */
function newCartApiClient(baseUrl = '/') {
  const headers = {
    'Content-Type': 'application/json'
  }

  return {
    /**
     * Get the cart
     * @returns {Object} the cart
     * @throws {Error} if operation fails
     */
    async getCart() {
      try {
        const url = buildURL('/cart.js')
        const res = await fetch(url, { headers })
        if (!res.ok) {
          throw newResponseError(res)
        }
        const cart = await res.json()
        return cart
      } catch (error) {
        throw new Error('Failed to get cart', { cause: error })
      }
    },
    /**
     * Update the cart
     * @param {Object} updates - updates to apply to the cart
     * @param {string} sections - (optional) section ids to fetch for ajax (comma separated string)
     * @returns {Object} the updated cart
     * @throws {Error} if operation fails
     */
    async updateCart(updates, sections) {
      try {
        const url = buildURL('/cart/update.js')
        const body = JSON.stringify({ updates, sections })
        const res = await fetch(url, { method: 'POST', body, headers })
        if (!res.ok) {
          throw newResponseError(res)
        }
        const result = await res.json()
        return result
      } catch (error) {
        throw new Error('Failed to update cart', { cause: error })
      }
    },
    /**
     * Add line items to the cart
     * @param {Array} lineItems - line items to add to the cart
     * @param {string} sections - (optional) section ids to fetch for ajax (comma separated string)
     * @returns {Array} the added line items
     * @throws {Error} if operation fails
     */
    async addLineItems(items, sections) {
      try {
        const url = buildURL('/cart/add.js')
        const body = JSON.stringify({ items, sections })
        const res = await fetch(url, { method: 'POST', body, headers })
        if (!res.ok) {
          throw newResponseError(res)
        }
        const result = await res.json()
        return result
      } catch (error) {
        throw new Error('Failed to add to cart', { cause: error })
      }
    },

    /**
     * Update a line item in the cart
     * @param {Object} input - the line item update input, including `id` and `quantity`
     * @param {string} sections - (optional) section ids to fetch for ajax (comma separated string)
     * @returns {Object} the updated cart
     * @throws {Error} if operation fails
     */
    async updateLineItem(input, sections) {
      try {
        const url = buildURL('/cart/change.js')
        const body = JSON.stringify({ ...input, sections, id: String(input.id) })
        const res = await fetch(url, { method: 'POST', body, headers })
        if (!res.ok) {
          throw newResponseError(res)
        }
        const result = await res.json()
        return result
      } catch (error) {
        throw new Error('Failed to update line item', { cause: error })
      }
    }
  }

  function buildURL(path) {
    return `${baseUrl.replace(/\/$/, '')}${path}`
  }

  function newResponseError(response) {
    return new Error(`Request failed with status ${response.status} ${response.statusText}`)
  }
}
